// FulfillmentStepper — Apple-style horizontal progress indicator for order
// fulfillment: Placed -> Processing -> Shipped -> Delivered (design.md AC4,
// T6). Rendered on OrderDetailView.
//
// Derives PURELY from the order's CURRENT status — there is no per-stage
// history in this data (no ship date, no delivery date), so this component
// never shows or implies a date; it only shows which named stage the order
// has reached.
//
// STATUS -> STEP MAPPING:
//   pending, pending_payment -> step 0 "Placed"
//   confirmed, processing    -> step 1 "Processing"
//   shipped                  -> step 2 "Shipped"
//   delivered                -> step 3 "Delivered" (terminal: renders as ALL
//                                                    four steps complete,
//                                                    not a "current" step
//                                                    still in progress)
//
// CANCELLED / REFUNDED (design.md AC4 explicitly leaves these undecided —
// "Decide and document what refunded and pending render... the spec doesn't
// say. Pick the honest option"): BOTH render a banner INSTEAD of a stepper
// position, never a synthetic step, because showing ANY step for either
// would fabricate history this data does not carry:
//   - cancelled: the order stopped at an UNKNOWN point before completion.
//     Showing e.g. "Processing" as reached would claim a fact (it got that
//     far) status alone cannot verify. -> error-tone banner, mirroring the
//     error-tone badge cancelled gets from the shared status map
//     (embed-shared/status.ts via EmbedStatusBadge).
//   - refunded: a refund can happen before OR after shipment (chargeback,
//     pre-fulfillment cancellation-refund, or a return after delivery) — the
//     same "unknown lifecycle position" problem as cancelled, for a
//     different reason. Refunded is not inherently a failure the way
//     cancelled is (it can be a routine customer-initiated return), so it
//     gets a NEUTRAL banner, not the alarming error/red one.
// PENDING is deliberately NOT given the banner treatment: unlike
// cancelled/refunded it IS a genuine point on the normal forward path (the
// earliest one) — step 0 is not fabricating anything, it is simply naming
// where a brand-new order legitimately sits.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens(). Purely
// informational (no click-to-navigate, unlike CheckoutStepper), so this
// renders plain spans, not <button>s — nothing here is interactive.

import type { ReactNode } from "react";
import { useEmbedTokens } from "../../embed-shared/primitives";
import type { OrderStatus } from "../../embed-shared/types";

const STEP_LABELS = ["Placed", "Processing", "Shipped", "Delivered"] as const;

export type FulfillmentDisplay =
  | { kind: "stepper"; stepIndex: number }
  | { kind: "banner"; tone: "cancelled" | "refunded" };

// Pure derivation, exported and unit-tested independent of rendering (T6
// test requirement: "stepper state derivation for every status including
// cancelled/refunded/pending").
export function deriveFulfillmentDisplay(status: OrderStatus): FulfillmentDisplay {
  switch (status) {
    case "cancelled":
      return { kind: "banner", tone: "cancelled" };
    case "refunded":
      return { kind: "banner", tone: "refunded" };
    case "pending":
    case "pending_payment":
      return { kind: "stepper", stepIndex: 0 };
    case "confirmed":
    case "processing":
      return { kind: "stepper", stepIndex: 1 };
    case "shipped":
      return { kind: "stepper", stepIndex: 2 };
    case "delivered":
      return { kind: "stepper", stepIndex: 3 };
    default: {
      // Exhaustiveness guard: a new OrderStatus value fails the build here
      // instead of silently falling through to an undefined display.
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

function Banner({ tone, children }: { tone: "cancelled" | "refunded"; children: ReactNode }) {
  const t = useEmbedTokens();
  // "cancelled" reuses the same error palette as EmbedBanner tone="error"
  // (assertive, matches the error-tone status badge). "refunded" is
  // deliberately NOT error/success — EmbedBanner only offers those two
  // tones and neither fits (see file header) — so this is a local neutral
  // variant with the same shape rather than a new EmbedBanner tone (keeps
  // this change out of the shared primitives file).
  const palette =
    tone === "cancelled"
      ? { bg: t.colors.errorBg, border: t.colors.error, text: t.colors.error }
      : { bg: t.colors.surfaceSunken, border: t.colors.cardBorder, text: t.colors.textSecondary };
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "center",
        gap: t.spacing.sm,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        borderRadius: t.borderRadius,
        padding: `${t.spacing.sm}px ${t.spacing.md}px`,
        fontSize: t.fontSize.sm,
        lineHeight: t.lineHeight.normal,
        boxSizing: "border-box",
      }}
    >
      {children}
    </div>
  );
}

export interface FulfillmentStepperProps {
  status: OrderStatus;
}

export function FulfillmentStepper({ status }: FulfillmentStepperProps) {
  const t = useEmbedTokens();
  const display = deriveFulfillmentDisplay(status);

  if (display.kind === "banner") {
    return (
      <Banner tone={display.tone}>
        {display.tone === "cancelled" ? "This order was cancelled." : "A refund was recorded for this order."}
      </Banner>
    );
  }

  const { stepIndex } = display;
  const N = STEP_LABELS.length;
  // Same continuous-track geometry as CheckoutStepper: the track spans from
  // the first circle's center to the last circle's center.
  const trackInsetPct = 50 / N;
  const trackTop = 11; // center of 24px circle minus 1px (2px line)
  const fillWidthPct = N > 1 ? (stepIndex / (N - 1)) * (100 - 100 / N) : 0;

  return (
    <nav aria-label="Order fulfillment progress" style={{ width: "100%" }}>
      <div style={{ position: "relative" }}>
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: trackTop,
            left: `${trackInsetPct}%`,
            right: `${trackInsetPct}%`,
            height: 2,
            backgroundColor: t.colors.stepperPending,
            borderRadius: 1,
          }}
        />
        {stepIndex > 0 && (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: trackTop,
              left: `${trackInsetPct}%`,
              width: `${fillWidthPct}%`,
              height: 2,
              backgroundColor: t.colors.stepperComplete,
              borderRadius: 1,
            }}
          />
        )}
        <ol style={{ display: "flex", listStyle: "none", margin: 0, padding: 0, gap: 0 }}>
          {STEP_LABELS.map((label, idx) => {
            // The terminal step (Delivered, reached) reads as complete, not
            // "current and still in progress" — delivery is a finished fact,
            // unlike an intermediate stage the order is presently sitting in.
            const isTerminalReached = idx === stepIndex && idx === N - 1;
            const isCompleted = idx < stepIndex || isTerminalReached;
            const isCurrent = idx === stepIndex && !isTerminalReached;
            const isPending = !isCompleted && !isCurrent;
            const circleColor = isCompleted
              ? t.colors.stepperComplete
              : isCurrent
                ? t.colors.stepperActive
                : t.colors.stepperPending;
            const stateSuffix = isCompleted ? " (completed)" : isCurrent ? " (current)" : "";

            return (
              <li
                key={label}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={`Step ${idx + 1}: ${label}${stateSuffix}`}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {/* TD-575 (Task 12): pending = outlined circle, number in secondary text; completed check in onAction (see CheckoutStepper). */}
                {/* Step circle — checkmark glyph (not just color) distinguishes
                    completed from current/pending for non-colour perception. */}
                <span
                  aria-hidden="true"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    backgroundColor: isPending ? t.colors.cardBg : circleColor,
                    color: isPending ? t.colors.textSecondary : t.colors.onAction,
                    boxSizing: "border-box",
                    border: isPending ? `1px solid ${t.colors.border}` : "none",
                    fontSize: t.fontSize.xs,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 1,
                    fontFamily: t.fontFamily,
                  }}
                >
                  {isCompleted ? "✓" : idx + 1}
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: t.fontSize.xs,
                    fontWeight: isCurrent ? 600 : 400,
                    color: isPending ? t.colors.textSecondary : t.colors.text,
                    marginTop: t.spacing.xs,
                  }}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
